import classNames from "classnames";
import { FiliedIconInactive } from "components/icons/FiliedIconInactive";
import { FilledGreenWiteTick } from "components/icons/FilledGreenWhiteTick";
import { Inprogress } from "components/icons/InProgress";
import { NotPurchased } from "components/icons/NotPurchased";
import { AcceptDocumentIcon } from "components/icons/JouneyTooltip/AcceptDocumentIcon";
import { FileEditIcon } from "components/icons/JouneyTooltip/FileEditIcon";
import { FileExclamationIcon } from "components/icons/JouneyTooltip/FileExclamationIcon";
import { FileSearchAlt } from "components/icons/JouneyTooltip/FileSearchAlt";
import { InQueueIcon } from "components/icons/JouneyTooltip/InQueueIcon";
import { OnHoldIcon } from "components/icons/JouneyTooltip/OnHoldIcon";
import { TaskChecklistIcon } from "components/icons/JouneyTooltip/TaskChecklistIcon";
import { UserTrustIcon } from "components/icons/JouneyTooltip/UserTrustIcon";
import {
  AMENDMENT_FILED,
  COMPLETED,
  IN_PROGRESS,
  SUCCESSFULLY_FILED,
} from "constants/taxValues";
import { JSX } from "react";
import { TaskStepDetails } from "types/Models/task";
import {
  STAGE_KEY_DRAFT_SHARED,
  STAGE_KEY_IN_QUEUE,
  STAGE_KEY_MISSING_INFORMATION,
  STAGE_KEY_PREP_IN_PROGRESS,
  STAGE_KEY_PREPARER_ASSIGNED,
  STAGE_KEY_READY_TO_FILE,
  STAGE_KEY_REVIEW_IN_PROGRESS,
} from "constants/clientProgressStageKeys";

type JourneyStepsProps = {
  stepDetails: TaskStepDetails[];
};

export const STAGE_ICONS: Record<string, JSX.Element> = {
  [STAGE_KEY_IN_QUEUE]: <InQueueIcon />,
  [STAGE_KEY_PREPARER_ASSIGNED]: <UserTrustIcon />,
  [STAGE_KEY_PREP_IN_PROGRESS]: <FileEditIcon />,
  [STAGE_KEY_MISSING_INFORMATION]: <FileExclamationIcon />,
  [STAGE_KEY_REVIEW_IN_PROGRESS]: <FileSearchAlt />,
  [STAGE_KEY_DRAFT_SHARED]: <TaskChecklistIcon />,
  [STAGE_KEY_READY_TO_FILE]: <AcceptDocumentIcon />,
};

const getIcon = (
  step: { name: string; key?: string },
  isCompleted: boolean
) => {
  if (step.name === IN_PROGRESS) {
    return <Inprogress />;
  }
  if (isCompleted) {
    return <FiliedIconInactive />;
  }
  return (
    (step.key ? STAGE_ICONS[step.key] : null) ??
    STAGE_ICONS[step.name] ?? <NotPurchased />
  );
};

export const JourneySteps = ({ stepDetails }: JourneyStepsProps) => (
  <>
    {stepDetails?.flatMap((step, index) => {
      const isCompleted =
        step.name === SUCCESSFULLY_FILED ||
        step.name === COMPLETED ||
        step.name === AMENDMENT_FILED ||
        step.isCompleted;
      const isInProgress = step.name === IN_PROGRESS;
      const isLast = index === stepDetails.length - 1;

      const stepRow = (
        <div className="t-flex t-gap-2" key={index}>
          <div className="t-flex t-flex-col t-items-center">
            <div className="t-flex-shrink-0">
              {step.isCompleted || step.isOnHold ? (
                <FilledGreenWiteTick />
              ) : (
                <span
                  className={classNames({
                    "t-text-blue-50": step.isCurrent && isInProgress,
                    "t-text-green": step.isCurrent && isCompleted,
                    "t-text-orange":
                      step.isCurrent && !isCompleted && !isInProgress,
                    "t-text-text-30": step.isIncomplete,
                  })}
                >
                  {getIcon(step, isCompleted)}
                </span>
              )}
            </div>
            {(!isLast || step.isOnHold) && (
              <div className="t-w-px t-flex-1 t-bg-white t-my-1" />
            )}
          </div>
          <div
            className={classNames("t-flex t-flex-col t-gap-0.5 t-min-w-0", {
              "t-pb-4": !isLast,
            })}
          >
            <div
              className={classNames(
                "t-text-left t-mt-0.5 t-flex t-items-center t-gap-2 t-w-full",
                {
                  "t-text-blue-50":
                    step.isCurrent && !step.isOnHold && isInProgress,
                  "t-text-green":
                    step.isCurrent && !step.isOnHold && isCompleted,
                  "t-text-orange":
                    step.isCurrent &&
                    !step.isOnHold &&
                    !isCompleted &&
                    !isInProgress,
                  "t-text-text-30": step.isIncomplete,
                }
              )}
            >
              <span>
                {step.name === "Pricing"
                  ? "Paid"
                  : step.name === "Completed"
                  ? "Filed"
                  : step.name}
              </span>
            </div>
            {step.isCurrent && !step.isOnHold && step.description && (
              <div className="t-text-body-sm t-text-text-30 t-text-left">
                {step.description}
              </div>
            )}
          </div>
        </div>
      );

      if (step.isOnHold) {
        return [
          stepRow,
          <div className="t-flex t-gap-2" key={`${index}-hold`}>
            <div className="t-flex t-flex-col t-items-center">
              <div className="t-flex-shrink-0 t-text-orange">
                <OnHoldIcon />
              </div>
              {!isLast && <div className="t-w-px t-flex-1 t-bg-white t-my-1" />}
            </div>
            <div
              className={classNames("t-flex t-flex-col t-gap-0.5 t-min-w-0", {
                "t-pb-5": !isLast,
              })}
            >
              <div className="t-text-orange t-text-left t-mt-0.5">On hold</div>
              {step.description && (
                <div className="t-text-body-sm t-text-text-30 t-text-left">
                  {step.description}
                </div>
              )}
            </div>
          </div>,
        ];
      }

      return [stepRow];
    })}
  </>
);
